/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
import React, { useCallback, useMemo, useRef, useState } from 'react';

import styled from 'styled-components';

import useProfileSizes from '~/pdp/utils/useProfileSizes';
import { useQuickViewContext } from '~/quickView/components/QuickViewContext';
import featureNames from '~/shared/constants/featureFlags';
import useHasFeature from '~/shared/utils/useHasFeature';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { Link } from '~/techstyle-shared/next-routes';
import { FormattedMessage } from '~/techstyle-shared/react-intl';
import { ProductType } from '~/techstyle-shared/react-products';

import { MAX_CARD_WIDTH } from '../../constants';
import useGridInputs from '../../hooks/useGridInputs';
import useProductCardTracking from '../../hooks/useProductCardTracking';
import useUnifiedProductGridData from '../../hooks/useUnifiedProductGridData';
import {
  getActiveProduct,
  getPSRC,
  getSwatchesWithPdpLink,
  shouldShowPlusSizeImageForProductCard,
} from '../../utils/activeProduct';

import ProductCardCalloutOverlay from './ProductCardCalloutOverlay';
import ProductCardFavoriteButton from './ProductCardFavoriteButton';
import ProductCardImage from './ProductCardImage';
import ProductCardImageLockedTreatmentWrapper from './ProductCardImageLockedTreatmentWrapper';
import ProductCardPricing from './ProductCardPricing';
import ProductCardSwatches from './ProductCardSwatches';
import ProductName from './ProductName';

const TrackedLink = styled.a`
  display: block;
  width: 100%;
  position: relative;
`;

const ProductCardContainer = styled.div`
  position: relative;
  max-width: ${({ $isFullWidth }) =>
    $isFullWidth ? 'none' : `${MAX_CARD_WIDTH}px`};
  height: 100%;
`;

const ProductCellWrapper = styled.div`
  margin-top: 8px;
`;

const QuickShopLabel = styled.div`
  cursor: pointer;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  min-width: 0;
  width: 100%;
  height: 32px;
  background-color: ${({ theme }) => theme.colors.palette.black};
  color: ${({ theme }) => theme.colors.palette.white};
  border-radius: 4px;
  padding-top: 8px;
  padding-bottom: 8px;
  position: absolute;
  bottom: 16px;

  ${({ theme }) => theme.productCard.quickShopLabel};

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.semanticColors.active};
  }
`;

const ProductCard = ({
  relatedProductsData,
  facets,
  id,
  gridIndex,
  ...rest
}) => {
  const [activeProductId, setActiveProductId] = useState(id);
  const { productSource, gridType, isQuickViewEnabled } = useGridInputs();
  const { selectedUrlFilters } = useUnifiedProductGridData();
  const originalColor = useRef();
  const secondarySite = useSecondarySite();
  const { enabled: isNewPricingUiEnabled } = useHasFeature(
    featureNames.PRICING_UI_AB
  );
  const isSecondarySite = useSecondarySite();
  // Disable new pricing UI for Checkout Interstitial pages (quickview grids)
  const shouldShowNewPricingUI = isNewPricingUiEnabled && !isQuickViewEnabled;

  // TODO: add isClearanceItem Logic to ProductName?
  // isClearanceItem={isClearanceItem}
  // TODO: ProductName - question re: prefix and suffix (see below story for context)
  // src/techstyle-shared/react-components/src/ProductName/ProductName.stories.js

  const { profileSizes } = useProfileSizes({ isPdp: false });
  const showPlusSizeImage = shouldShowPlusSizeImageForProductCard({
    productFacets: facets,
    selectedUrlFilters,
    profileSizes,
    gridType,
  });

  // primaryProductData identity changes every render due to ...rest spread; see each hook comment
  // eslint-disable-next-line react-hooks/exhaustive-deps -- object spread creates new identity each render
  const primaryProductData = { id, ...rest };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- primaryProductData changes every render due to ...rest spread
  const activeProduct = useMemo(() => {
    const activeProductData = getActiveProduct({
      relatedProducts: relatedProductsData?.relatedProducts,
      swatches: relatedProductsData?.swatches,
      primaryProductData,
      productSource,
      activeProductId,
      gridType,
    });

    if (!originalColor.current) {
      originalColor.current = activeProductData?.swatchLabel;
    }

    return activeProductData;
  }, [
    relatedProductsData,
    primaryProductData,
    productSource,
    activeProductId,
    gridType,
  ]);

  const quickViewContext = useQuickViewContext();
  const openQuickViewModal = quickViewContext?.openQuickViewModal;
  const activeProductPdpLink = useMemo(() => {
    const routePrefix = secondarySite ? `/${secondarySite}` : '';
    return `${routePrefix}${activeProduct?.pdpLink}`;
  }, [secondarySite, activeProduct]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- primaryProductData changes every render due to ...rest spread
  const swatchesWithPdpLink = useMemo(
    () =>
      getSwatchesWithPdpLink({
        swatches: relatedProductsData?.swatches,
        productSource,
        primaryProductData,
        relatedProducts: relatedProductsData?.relatedProducts,
        gridType,
      }),
    [relatedProductsData, primaryProductData, productSource, gridType]
  );

  const {
    trackProductClick,
    productCardRef,
    trackingTags,
    trackProductSwatchClick: baseTrackProductSwatchClick,
  } = useProductCardTracking({
    activeProduct,
    productSource,
    gridIndex,
  });

  // Bundles do not have swatch images. The swatch urls may exist from the APIs (or are manually constructed),
  // but the image binary is not available when trying to fetch from the CDN.
  // If the primary product is a bundle, we do not show the swatches, even for related products
  const showProductCardSwatches =
    activeProduct?.productTypeId !== ProductType.BUNDLE && !isSecondarySite;

  // eslint-disable-next-line react-hooks/exhaustive-deps -- primaryProductData changes every render due to ...rest spread
  const trackProductSwatchClick = useCallback(
    (swatch) => {
      const {
        permalink,
        swatchLabel,
        masterProductId,
        itemNumber,
        dateExpected,
        tagIdList,
      } = getActiveProduct({
        primaryProductData,
        relatedProducts: relatedProductsData?.relatedProducts,
        activeProductId: swatch?.productId,
        productSource,
        gridType,
      });
      const { url: swatchUrl } = swatch;

      const trackingData = {
        permalink,
        swatchUrl,
        masterProductId,
        itemNumber,
        dateExpected,
        tagIdList,
        color: swatchLabel,
        originalColor: originalColor.current,
      };

      baseTrackProductSwatchClick(trackingData);
    },
    [
      primaryProductData,
      relatedProductsData,
      productSource,
      gridType,
      baseTrackProductSwatchClick,
    ]
  );

  const handleQuickViewClick = (e) => {
    if (isQuickViewEnabled) {
      e.preventDefault();
      e.stopPropagation();

      openQuickViewModal(e, {
        product: activeProduct,
        psrc: getPSRC({ psrc: productSource, gridType }),
      });
    }
  };

  const handleLinkClick = (e) => {
    if (isQuickViewEnabled) {
      e.preventDefault();
    }
    trackProductClick(e);
  };

  return (
    <ProductCardContainer
      $isFullWidth={shouldShowNewPricingUI}
      ref={productCardRef}
      onClick={handleQuickViewClick}
      {...trackingTags}
    >
      {!isSecondarySite && (
        <ProductCardFavoriteButton product={activeProduct} />
      )}
      <Link href={activeProductPdpLink}>
        <TrackedLink onClick={handleLinkClick}>
          <ProductCardImageLockedTreatmentWrapper
            tagIdList={activeProduct?.tagIdList}
          >
            <ProductCardImage
              imageServiceUrl={activeProduct?.imageServiceUrl}
              swatchLabel={activeProduct?.swatchLabel}
              images={activeProduct?.images}
              sku={activeProduct?.sku}
              isPrimaryProduct={Number(activeProduct?.id) === Number(id)}
              showPlusSizeImage={showPlusSizeImage}
              permalink={activeProduct?.permalink}
            />
          </ProductCardImageLockedTreatmentWrapper>
          <ProductCardCalloutOverlay product={activeProduct} />
        </TrackedLink>
      </Link>
      <ProductCellWrapper>
        {showProductCardSwatches && (
          <ProductCardSwatches
            swatches={swatchesWithPdpLink}
            activeProduct={activeProduct}
            activeProductId={activeProductId}
            setActiveProductId={setActiveProductId}
            trackProductSwatchClick={trackProductSwatchClick}
          />
        )}
        <Link href={activeProductPdpLink}>
          <TrackedLink onClick={handleLinkClick}>
            <ProductName isNewPricingUiEnabled={shouldShowNewPricingUI}>
              {activeProduct?.label.replace(/&amp;/g, '&')}
            </ProductName>
            <ProductCardPricing
              product={activeProduct}
              isNewPricingUiEnabled={shouldShowNewPricingUI}
            />
          </TrackedLink>
        </Link>
      </ProductCellWrapper>

      {isQuickViewEnabled && (
        <QuickShopLabel data-autotag="productcard-add-to-bag-button">
          <FormattedMessage
            id="grid.move_to_bag"
            defaultMessage="MOVE TO BAG"
          />
        </QuickShopLabel>
      )}
    </ProductCardContainer>
  );
};

export default ProductCard;
